import type { Dot, Stick } from '@tools/math/Verlet';
import type { LifeCycleEntity } from '@tools/LifeCycle';
import { Snap } from './Snap';
import { Modifier } from './Modifier';

export interface DragOptions {
	throwStrength: number;
	throwStiffness: number;
	dragStiffness: number;
}

export class Drag extends Modifier implements LifeCycleEntity {
	public readonly options: DragOptions;

	private dragState: 'idle' | 'dragging' | 'throwing' = 'idle';

	private downStateX = -1;
	private downClientX = -1;
	private currentClientX = 0;
	private throwVelocity = 0;
	private latestDragDistance = 0;

	private mainDot!: Dot;
	private mainStick!: Stick;

	constructor(options: Partial<DragOptions> = {}) {
		super();

		this.lifeCycle.register(this);
		this.options = {
			throwStrength: 50,
			throwStiffness: 0.005,
			dragStiffness: 1,
			...options
		};
	}

	public isClickable() {
		return this.latestDragDistance < 10;
	}

	public async load() {
		const connection = this.createConnection('static');
		this.mainDot = connection.dot;
		this.mainDot.pinned = true;
		this.mainStick = connection.stick;
		this.mainStick.stiffness = 0;
		this.prop.touchAction = 'pan-y';
	}
	public async unload() {
		this.prop.touchAction = 'unset';
	}

	public async awake() {
		this.mainStick.stiffness = 0;

		// TODO: Create a tool for interaction events
		this.connection.containerElement?.addEventListener('pointerdown', this.onPointerDown);
		this.connection.containerElement?.addEventListener('wheel', this.onWheel, false);
	}
	public async sleep() {
		this.connection.containerElement?.removeEventListener('pointerdown', this.onPointerDown);
		this.connection.containerElement?.removeEventListener('wheel', this.onWheel, false);
	}

	public async stop() {
		this.mainDot.pos.x = this.state.drag;
		this.mainStick.stiffness = 0;

		window.removeEventListener('pointermove', this.onPointerMove);
		window.removeEventListener('touchmove', this.onTouchMove);
		window.removeEventListener('mouseup', this.onUp);
		window.removeEventListener('touchend', this.onUp);
	}

	private onPointerDown = (e: MouseEvent) => {
		this.takeover();
		this.dragState = 'dragging';
		this.prop.cursor = 'grabbing';
		this.downClientX = e.clientX;
		this.currentClientX = e.clientX;
		this.downStateX = this.state.drag;
		this.mainStick.stiffness = this.options.dragStiffness;
		this.mainDot.pos.x = this.downStateX;
		this.closeAtTick = 0;
		this.latestDragDistance = 0;
		this.mainDot.applyConstraints = false;

		window.addEventListener('pointermove', this.onPointerMove);
		window.addEventListener('touchmove', this.onTouchMove);
		window.addEventListener('mouseup', this.onUp);
		window.addEventListener('touchend', this.onUp);
	};

	private onWheel = (e: WheelEvent) => {
		// NOTE: We trust the wheel. To many egde cases, different wheels and touch pads.
		// So snapping is not applied when using scroll.
		// We could snap when the user stops scrolling?
		// Remember that touchpads have momentum scrolling applied.

		// if scrolling horizontally
		if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
			this.mainStick.stiffness = this.options.dragStiffness;
			let outOfBoundsStrength = this.getBoundsStrength();
			this.mainDot.pos.x = this.state.drag + e.deltaX * outOfBoundsStrength;
			e.preventDefault();
			e.stopPropagation();
		}
	};

	private getBoundsStrength() {
		let outOfBoundsStrength = 1;
		if (this.throwVelocity < 0) {
			if (this.mainDot.isOutOfBoundsLeft()) {
				outOfBoundsStrength = 0.2;
			}
		} else {
			if (this.mainDot.isOutOfBoundsRight()) {
				outOfBoundsStrength = 0.2;
			}
		}
		return outOfBoundsStrength;
	}

	private onUp = () => {
		window.removeEventListener('pointermove', this.onPointerMove);
		window.removeEventListener('touchmove', this.onTouchMove);
		window.removeEventListener('mouseup', this.onUp);
		window.removeEventListener('touchend', this.onUp);

		this.prop.cursor = 'grab';
		this.dragState = 'throwing';

		const outOfBoundsStrength = this.getBoundsStrength();
		const throwFrom = this.state.drag;
		let throwTo = throwFrom + this.throwVelocity * this.options.throwStrength * outOfBoundsStrength;

		const snapModifier = this.getModifier(Snap);
		if (snapModifier) {
			throwTo = snapModifier.snap(throwTo);
		}

		this.mainDot.applyConstraints = true;

		this.mainDot.pos.x = throwTo;
		this.mainStick.stiffness = this.options.throwStiffness;
	};

	private onTouchMove = (e: TouchEvent) => {
		const touch = e.touches[0];
		if (!touch) return;
		this.move(touch.clientX);
	};

	private onPointerMove = (e: MouseEvent) => {
		this.move(e.clientX);
	};

	private move = (to: number) => {
		if (this.currentClientX === to) return;
		let delta = to - this.downClientX;

		this.latestDragDistance += Math.abs(delta);
		this.throwVelocity = to - this.currentClientX;
		this.currentClientX = to;
		if (this.mainDot.isOutOfBounds()) {
			delta *= 0.2;
		}

		this.mainDot.pos.x = this.downStateX + delta;
	};

	private closeAtTick = 0;

	public override tick(): void {
		if (this.dragState === 'throwing') {
			const delta = Math.abs(this.mainDot.pos.x - this.state.drag);

			if (delta < 2) {
				this.closeAtTick++;
				if (this.closeAtTick > 10) {
					this.mainStick.stiffness = 0;
					this.giveback();
					this.closeAtTick = 0;
					this.dragState = 'idle';
				}
			}
		}

		this.throwVelocity *= 0.8;
	}
}
